import React, { useState } from 'react';
import Modal from '../ui/Modal';
import { Button, ButtonProps } from '../ui/Button';
import { cn } from '@/app/lib/utils';

interface ConfirmationTriggerProps {
  children?: React.ReactElement;
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: ButtonProps['color'];
  confirmVariant?: ButtonProps['variant'];
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  showClose?: boolean;
  closeOnClickOutside?: boolean;
  className?: string;
  disabled?: boolean;
  beforeOpen?: () => boolean | Promise<boolean>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ConfirmationTrigger: React.FC<ConfirmationTriggerProps> = ({
  children,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'blue',
  confirmVariant,
  onConfirm,
  onCancel,
  isLoading = false,
  showClose = false,
  closeOnClickOutside = true,
  className,
  disabled = false,
  beforeOpen,
  open,
  onOpenChange,
}: ConfirmationTriggerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = (nextOpen: boolean) => {
    if (open === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const handleCancel = () => {
    onCancel?.();
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onConfirm();
    if (open === undefined) {
      setIsOpen(false);
    }
  };

  const handleChildClick = async (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    if (beforeOpen) {
      const shouldOpen = await beforeOpen();
      if (!shouldOpen) {
        return;
      }
    }

    setIsOpen(true);
  };

  const isDestructive = confirmColor === 'destructive' || confirmVariant === 'destructive';

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
        {cancelText}
      </Button>
      <Button
        variant={confirmVariant ?? (isDestructive ? 'destructive' : 'default')}
        color={confirmColor}
        className={cn(isDestructive && '!text-white')}
        onClick={handleConfirm}
        loading={isLoading}
        disabled={isLoading}
      >
        {confirmText}
      </Button>
    </div>
  );

  return (
    <>
      {children && (
        <div onClick={handleChildClick} className={cn(className)}>
          {children}
        </div>
      )}
      <Modal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        showClose={showClose}
        closeOnClickOutside={closeOnClickOutside}
        bodyClassName="!max-w-md p-6"
      >
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
            {typeof description === 'string' ? <p>{description}</p> : description}
          </div>
          <div className="mt-6">{footer}</div>
        </div>
      </Modal>
    </>
  );
};

export default ConfirmationTrigger;
